// src/pages/ExpenseList.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllExpenses, getAllAccounts, addExpense, updateExpense, deleteExpense } from "../firebase/database";

export default function ExpenseList() {
  const [expenses, setExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    accountId: "",
    accountName: "",
    amount: 0,
    description: "",
    paymentMethod: "",
  });
  const [saving, setSaving] = useState(false);
  const [filterAccount, setFilterAccount] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [expensesData, accountsData] = await Promise.all([
        getAllExpenses(),
        getAllAccounts(),
      ]);
      setExpenses(expensesData);
      setAccounts(accountsData.filter(a => a.nature === "Expense" || a.nature === "Cash" || a.nature === "Bank"));
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "accountId") {
      const account = accounts.find(a => a.id === value);
      setFormData(prev => ({ ...prev, accountId: value, accountName: account?.name || "" }));
    } else {
      setFormData(prev => ({ ...prev, [name]: name === "amount" ? parseFloat(value) || 0 : value }));
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      accountId: "",
      accountName: "",
      amount: 0,
      description: "",
      paymentMethod: "",
    });
    setEditingExpense(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accountId || formData.amount <= 0) {
      alert("Please select an account and enter amount");
      return;
    }

    setSaving(true);
    try {
      if (editingExpense) {
        await updateExpense(editingExpense.id, formData);
      } else {
        await addExpense(formData);
      }
      await loadData();
      resetForm();
    } catch (err) {
      console.error("Error saving expense:", err);
      alert("Error saving expense");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date || new Date().toISOString().split("T")[0],
      accountId: expense.accountId || "",
      accountName: expense.accountName || "",
      amount: expense.amount || 0,
      description: expense.description || "",
      paymentMethod: expense.paymentMethod || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this expense?")) return;
    try {
      await deleteExpense(id);
      await loadData();
    } catch (err) {
      console.error("Error deleting expense:", err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN");
  };

  const filteredExpenses = filterAccount 
    ? expenses.filter(e => e.accountId === filterAccount)
    : expenses;

  const totalAmount = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
        <div style={{ color: "#6B7280" }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      <style>{`
        .expense-header { display: flex; justify-content: space-between; align-items: center; marginBottom: 24px; gap: 16px; }
        .expense-table-container { background: white; border-radius: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); overflow: hidden; }
        .expense-mobile-cards { display: none; flex-direction: column; gap: 12px; }
        .expense-card { background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 12px; boxShadow: 0 1px 3px rgba(0,0,0,0.1); }
        .expense-card-header { display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #F3F4F6; padding-bottom: 10px; }
        .expense-card-info { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 13px; }
        .info-label { color: #6B7280; font-size: 11px; font-weight: 600; text-transform: uppercase; }
        .expense-card-actions { display: flex; gap: 8px; flex-wrap: wrap; padding-top: 10px; border-top: 1px solid #F3F4F6; }

        @media (max-width: 768px) {
          .expense-header { flex-direction: column; align-items: flex-start; }
          .expense-header > button { width: 100%; }
          .expense-filters { flex-direction: column; align-items: stretch !important; gap: 10px !important; }
          .expense-table-container { display: none; }
          .expense-mobile-cards { display: flex; }
        }
      `}</style>

      <div className="expense-header" style={{ marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            <Link to="/admin" style={{ color: "#6B7280", textDecoration: "none", display: "flex", alignItems: "center" }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: "#1F2937", margin: 0 }}>Expense Vouchers</h1>
          </div>
          <p style={{ color: "#6B7280", margin: "4px 0 0 44px" }}>Record and manage expense entries</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          style={{ padding: "12px 24px", background: "#3B82F6", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
        >
          + Add Expense
        </button>
      </div>

      {/* Filters */}
      <div className="expense-filters" style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "center" }}>
        <select
          value={filterAccount}
          onChange={(e) => setFilterAccount(e.target.value)}
          style={{ padding: "10px 16px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, flex: 1, maxWidth: 300 }}
        >
          <option value="">All Accounts</option>
          {accounts.filter(a => a.nature === "Expense").map(a => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <div style={{ padding: "10px 16px", background: "#FEE2E2", borderRadius: 8, fontSize: 14, fontWeight: 600, color: "#DC2626", textAlign: "center" }}>
          Total: {formatCurrency(totalAmount)}
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
          <div style={{ background: "white", borderRadius: 12, padding: 24, width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: "#1F2937", marginBottom: 20 }}>
              {editingExpense ? "Edit Expense" : "Add New Expense"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Date *</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Expense Account *</label>
                <select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleChange}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}
                >
                  <option value="">Select Account</option>
                  {accounts.filter(a => a.nature === "Expense").map(a => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Amount (₹) *</label>
                <input
                  type="number"
                  name="amount"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Payment From</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}
                >
                  <option value="">Select Payment Method</option>
                  {accounts.filter(a => a.nature === "Cash" || a.nature === "Bank").map(a => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 14, fontWeight: 500, color: "#374151", marginBottom: 6 }}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14, resize: "vertical" }}
                  placeholder="Enter description..."
                />
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={resetForm}
                  style={{ padding: "10px 20px", background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{ padding: "10px 20px", background: saving ? "#9CA3AF" : "#3B82F6", color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: saving ? "not-allowed" : "pointer" }}
                >
                  {saving ? "Saving..." : editingExpense ? "Update" : "Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="expense-table-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>VOUCHER NO</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>DATE</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>ACCOUNT</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>DESCRIPTION</th>
              <th style={{ padding: "14px 20px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>PAYMENT FROM</th>
              <th style={{ padding: "14px 20px", textAlign: "right", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>AMOUNT</th>
              <th style={{ padding: "14px 20px", textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6B7280" }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: "center", color: "#6B7280" }}>
                  No expenses yet. Click "Add Expense" to create one.
                </td>
              </tr>
            ) : (
              filteredExpenses.map((expense, index) => (
                <tr key={expense.id} style={{ borderBottom: index < filteredExpenses.length - 1 ? "1px solid #E5E7EB" : "none" }}>
                  <td style={{ padding: "14px 20px", fontWeight: 600, color: "#1F2937" }}>{expense.voucherNo}</td>
                  <td style={{ padding: "14px 20px", color: "#374151" }}>{formatDate(expense.date)}</td>
                  <td style={{ padding: "14px 20px", color: "#374151" }}>{expense.accountName}</td>
                  <td style={{ padding: "14px 20px", color: "#374151" }}>{expense.description || "-"}</td>
                  <td style={{ padding: "14px 20px", color: "#374151" }}>{expense.paymentMethod || "-"}</td>
                  <td style={{ padding: "14px 20px", textAlign: "right", fontWeight: 600, color: "#DC2626" }}>{formatCurrency(expense.amount)}</td>
                  <td style={{ padding: "14px 20px", textAlign: "center" }}>
                    <button onClick={() => handleEdit(expense)} style={{ padding: "6px 12px", fontSize: 12, background: "#E5E7EB", color: "#374151", borderRadius: 6, border: "none", cursor: "pointer", marginRight: 8 }}>Edit</button>
                    <button onClick={() => handleDelete(expense.id)} style={{ padding: "6px 12px", fontSize: 12, background: "#FEE2E2", color: "#DC2626", borderRadius: 6, border: "none", cursor: "pointer" }}>Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="expense-mobile-cards">
        {filteredExpenses.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6B7280", background: "white", borderRadius: 12 }}>
            No expenses found.
          </div>
        ) : (
          filteredExpenses.map((expense) => (
            <div key={expense.id} className="expense-card">
              <div className="expense-card-header">
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#1F2937" }}>{expense.voucherNo}</div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>{formatDate(expense.date)}</div>
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#DC2626" }}>{formatCurrency(expense.amount)}</div>
              </div>
              <div className="expense-card-info">
                <div style={{ gridColumn: "span 2" }}>
                  <div className="info-label">Account</div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{expense.accountName}</div>
                </div>
                <div>
                  <div className="info-label">Payment From</div>
                  <div style={{ fontWeight: 500 }}>{expense.paymentMethod || "—"}</div>
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <div className="info-label">Description</div>
                  <div style={{ fontSize: 13 }}>{expense.description || "No description"}</div>
                </div>
              </div>
              <div className="expense-card-actions">
                <button
                  onClick={() => handleEdit(expense)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#E5E7EB", color: "#374151", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(expense.id)}
                  style={{ flex: 1, padding: "8px 12px", fontSize: 13, background: "#FEE2E2", color: "#DC2626", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600 }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}